import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, FormProvider } from 'react-hook-form'

import axios from '../../dataClient/dataClient'
import { config } from '../../config'
import { useMultiFieldValidation } from '../../hooks/useMultiFieldValidation'
import { foiInfoSchema } from '../../schemas/foiSchema'
import { emptyFoiInfo } from '../../util/foiConsts'
import { ACCESS_METHOD_TYPE } from '../../util/foiAccessMethod'
import { InlineContainer, Form, FormSectionDiv, FlexColumn, SpaceBetweenRowContainer, FlexEndContainer } from '../styles/Containers'
import { BoldFormTitle } from '../styles/Text'
import { ErrorMessage } from '../common/UserMessage'
import Button, { SecondaryButton } from '../common/Button'
import FormSelect from '../inputFields/FormSelect'
import NameForm from '../foiForms/NameForm'
import ContactForm from '../foiForms/ContactForm'
import InformationTypeForm from '../foiForms/InformationTypeForm'
import RequestDetailsForm from '../foiForms/RequestDetailsForm'
import FOIDocumentAccessForm from '../foiForms/FOIDocumentAccessForm'

function EditableFOIRequestInfo({
  foiId,
  versionName,
  requestInfo = emptyFoiInfo,
  onSuccess = () => {},
  handleCancel = () => {},
  accessMethodConfig = config.foi.request.accessMethod
}) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)

  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(foiInfoSchema),
    defaultValues: requestInfo
  })
  const { handleSubmit, reset } = methods
  useMultiFieldValidation(methods, ['requestor.email', 'requestor.address'])
  useMultiFieldValidation(methods, ['requestor.address'], ['requestor.city', 'requestor.province', 'requestor.postalCode'])
  useMultiFieldValidation(methods, ['requestor.email', 'requestor.confirmEmail'], ['requestor.confirmEmail'])

  const onError = (errors, e) => console.error('Error', errors, e)

  function onCancel() {
    setError(false)
    reset({ ...requestInfo })
    handleCancel()
  }

  function onSubmit(data) {
    setIsLoading(true)
    setError(false)

    const foiData = structuredClone(data)
    if (!foiData.requestor.isBusiness) foiData.requestor.businessName = ''
    if (!foiData.hasAddresses) foiData.addresses = []
    if (!foiData.infoType || !foiData.infoType.includes('THIRD_PARTY')) foiData.thirdPartyFiles = []
    if (foiData.periodStart) foiData.periodStart = new Date(foiData.periodStart).toISOString()
    if (foiData.periodEnd) foiData.periodEnd = new Date(foiData.periodEnd).toISOString()
    if (foiData.existingThirdPartyFiles) foiData.thirdPartyFileIds = foiData.existingThirdPartyFiles.map(f => f.id)

    foiData.addresses = foiData.addresses.map(address => {
      const clonedAddress = { ...address }
      if (address.existingFiles) clonedAddress.fileIds = clonedAddress.existingFiles.map(f => f.id)
      delete clonedAddress.existingFiles

      return clonedAddress
    })

    const thirdPartyFiles = foiData.thirdPartyFiles || []
    const requestorData = { ...foiData.requestor }

    delete foiData.thirdPartyFiles
    delete foiData.existingThirdPartyFiles
    delete foiData.hasAddresses
    delete foiData.requestor

    const foiPayload = {
      requestor: requestorData,
      applicantType: requestorData.isBusiness ? 'COMMERCIAL' : 'PERSONAL',
      ...foiData
    }

    const fileData = new FormData()
    fileData.append('payload', JSON.stringify(foiPayload))

    thirdPartyFiles.forEach(file => {
      fileData.append('thirdPartyFiles', file, file.name)
    })

    const addressFilesMeta = []
    foiData.addresses.forEach((address, addressIndex) => {
      const { files = [], ...addressInfo } = address || {}
      if (files.length > 0) {
        addressFilesMeta.push({
          addressIndex,
          addressInfo: {
            ...addressInfo,
            files: files.map(f => ({ name: f.name, size: f.size }))
          }
        })
      }
      files.forEach(file => {
        fileData.append('addressFiles', file, file.name)
      })
    })

    fileData.append('addressFilesMeta', JSON.stringify(addressFilesMeta))

    axios
      .patch(`/foi/${foiId}/request`, fileData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(response => {
        onSuccess(response.data)
        setIsLoading(false)
      })
      .catch(e => {
        console.error('Failed to update the FOI request details', e)
        setError(true)
        setIsLoading(false)
      })
  }

  return (
    <FlexColumn>
      <FormProvider {...methods}>
        <InlineContainer>
          <Form onSubmit={handleSubmit(onSubmit, onError)}>
            <FlexColumn gap="10px">
              <SpaceBetweenRowContainer>
                <BoldFormTitle>Request</BoldFormTitle>
                <FlexEndContainer gap="10px">
                  <FormSelect
                    id="version"
                    options={[versionName]}
                    defaultValue={versionName}
                    minwidth="200px"
                    disabled
                    props={{ inputProps: { 'aria-label': 'Version' } }}
                  />
                  <SecondaryButton onClick={onCancel} disabled={isLoading}>
                    Cancel
                  </SecondaryButton>
                  <Button type="submit" loading={isLoading}>
                    Save
                  </Button>
                </FlexEndContainer>
              </SpaceBetweenRowContainer>
              {error && (
                <FlexEndContainer>
                  <ErrorMessage>There was an error updating the FOI request. Please try again.</ErrorMessage>
                </FlexEndContainer>
              )}
            </FlexColumn>
            <FormSectionDiv padding="10px 15px">
              <RequestDetailsForm format="condensed" perspective="staff" disabled={isLoading} />
            </FormSectionDiv>
            <FormSectionDiv padding="10px 15px">
              <InformationTypeForm format="condensed" perspective="staff" disabled={isLoading} />
            </FormSectionDiv>
            <FormSectionDiv padding="10px 15px">
              <NameForm format="condensed" perspective="staff" disabled={isLoading} />
            </FormSectionDiv>
            <FormSectionDiv padding="10px 15px">
              <ContactForm format="condensed" perspective="staff" disabled={isLoading} />
            </FormSectionDiv>
            <FormSectionDiv padding="10px 15px">
              <FOIDocumentAccessForm format="condensed" perspective="staff" disabled={isLoading} accessMethodConfig={accessMethodConfig} />
            </FormSectionDiv>
          </Form>
        </InlineContainer>
      </FormProvider>
    </FlexColumn>
  )
}

EditableFOIRequestInfo.propTypes = {
  foiId: PropTypes.string.isRequired,
  versionName: PropTypes.string.isRequired,
  requestInfo: PropTypes.object,
  onSuccess: PropTypes.func,
  handleCancel: PropTypes.func,
  accessMethodConfig: PropTypes.shape({
    digitalLabel: PropTypes.string.isRequired,
    physicalLabel: PropTypes.string.isRequired,
    otherActiveType: PropTypes.shape({
      type: PropTypes.oneOf([ACCESS_METHOD_TYPE.OTHER_DIGITAL, ACCESS_METHOD_TYPE.OTHER_PHYSICAL, ACCESS_METHOD_TYPE.OTHER_PICKUP]).isRequired,
      label: PropTypes.string.isRequired,
      pricingDetails: PropTypes.string
    })
  })
}

export default EditableFOIRequestInfo
